import { type FC, useCallback, useState } from "react";

import { Box, Card, Group, SimpleGrid, Skeleton, Text } from "@mantine/core";

import { NewTag } from "../../shared/components";
import { useSpotlightStories } from "../hooks";

import {
    emptyState,
    sectionHeader,
    sectionSubtitle,
    sectionTitle,
    videoCard,
    videoPlayer,
} from "./HomeMadeWithLensesSection.module.css";
import { SpotlightStoryCard } from "./SpotlightStoryCard";
import {
    MadeWithYourLenses,
    MadeWithYourLensesError,
    MadeWithYourLensesNoStories,
    MadeWithYourLensesSubtitle,
} from "./messages.generated";

const SKELETON_COUNT = 5;

export const HomeMadeWithLensesSection: FC = () => {
    const { stories, loading, error } = useSpotlightStories();
    const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());

    const handleVideoError = useCallback((storySnapId: string) => {
        setFailedUrls((prev) => (prev.has(storySnapId) ? prev : new Set(prev).add(storySnapId)));
    }, []);

    return (
        <Box>
            <div className={sectionHeader}>
                <Group gap={4}>
                    <h3 className={sectionTitle}>
                        <MadeWithYourLenses />
                    </h3>
                    <NewTag />
                </Group>
                <p className={sectionSubtitle}>
                    <MadeWithYourLensesSubtitle />
                </p>
            </div>
            <Card shadow="sm" radius={0} p="md">
                {loading ? (
                    <SimpleGrid cols={{ base: 2, xs: 5 }} spacing="xs">
                        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                            <div key={i} className={videoCard}>
                                <Skeleton className={videoPlayer} radius="md" />
                            </div>
                        ))}
                    </SimpleGrid>
                ) : error ? (
                    <Text className={emptyState}>
                        <MadeWithYourLensesError />
                    </Text>
                ) : stories.length === 0 ? (
                    <Text className={emptyState}>
                        <MadeWithYourLensesNoStories />
                    </Text>
                ) : (
                    <SimpleGrid cols={{ base: 2, xs: 5 }} spacing="xs">
                        {stories.map((story) => (
                            <SpotlightStoryCard
                                key={story.storySnapId}
                                storySnapId={story.storySnapId}
                                mediaUrl={story.mediaUrl}
                                views={story.views}
                                lensName={story.lensName}
                                lensIconUrl={story.lensIconUrl}
                                lensId={story.lensId}
                                orgId={story.orgId}
                                lensFolderId={story.lensFolderId}
                                onError={handleVideoError}
                                hasFailed={failedUrls.has(story.storySnapId)}
                            />
                        ))}
                    </SimpleGrid>
                )}
            </Card>
        </Box>
    );
};
